import { clipBoxEnabledAtom, clipBoxModeAtom, type ClipBoxMode } from '@/atoms/viewer.atom';
import { ClipMode, type IClipBox, type PointCloudOctree } from '@pnext/three-loader';
import { PivotControls } from '@react-three/drei';
import { ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

const CLIP_MODE_MAP: Record<Exclude<ClipBoxMode, 'NONE'>, ClipMode> = {
  CLIP_OUTSIDE: ClipMode.CLIP_OUTSIDE,
  HIGHLIGHT_INSIDE: ClipMode.HIGHLIGHT_INSIDE,
  CLIP_INSIDE: ClipMode.CLIP_INSIDE,
};

const MIN_BOX_SIZE = 0.5;

type FaceDef = { axis: 'x' | 'y' | 'z'; sign: 1 | -1 };

const FACES: FaceDef[] = [
  { axis: 'x', sign: 1 },
  { axis: 'x', sign: -1 },
  { axis: 'y', sign: 1 },
  { axis: 'y', sign: -1 },
  { axis: 'z', sign: 1 },
  { axis: 'z', sign: -1 },
];

const AXIS_COLORS: Record<string, string> = { x: '#ff6666', y: '#66ff66', z: '#6688ff' };

function getNDC(event: PointerEvent | MouseEvent, domElement: HTMLElement): THREE.Vector2 {
  const rect = domElement.getBoundingClientRect();
  return new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
}

/** Get the world-space position of a face handle. */
function getFaceWorldPos(
  face: FaceDef,
  center: THREE.Vector3,
  halfSize: THREE.Vector3,
  rotation: THREE.Quaternion,
): THREE.Vector3 {
  const localOffset = new THREE.Vector3();
  localOffset[face.axis] = face.sign * halfSize[face.axis];
  return localOffset.applyQuaternion(rotation).add(center);
}

export const ClipBoxTool = ({ pointClouds }: { pointClouds: PointCloudOctree[] }) => {
  const enabled = useAtomValue(clipBoxEnabledAtom);
  const mode = useAtomValue(clipBoxModeAtom);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const controls = useThree((state) => state.controls) as any;
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  // World-space center of the box
  const [boxCenter, setBoxCenter] = useState(() => new THREE.Vector3());
  // Local half-extents (before rotation)
  const [boxHalfSize, setBoxHalfSize] = useState(() => new THREE.Vector3(1, 1, 1));
  const [initialized, setInitialized] = useState(false);

  // Rotation quaternion — single source of truth for box orientation
  const rotationRef = useRef(new THREE.Quaternion());

  // Counter to force-remount PivotControls when center changes from face/translate drag
  const [pivotKey, setPivotKey] = useState(0);

  // Base rotation at the time PivotControls was last remounted.
  // PivotControls starts from identity after remount, so we compose its drag with this.
  const baseRotationRef = useRef(new THREE.Quaternion());

  // Wireframe ref for imperative quaternion updates
  const wireframeRef = useRef<THREE.LineSegments | null>(null);

  // 6 face handles + 1 center handle = 7
  const handleRefs = useRef<(THREE.Mesh | null)[]>(new Array(7).fill(null));
  const dragRef = useRef<{
    type: 'face' | 'translate';
    face?: FaceDef;
    plane: THREE.Plane;
    startIntersection: THREE.Vector3;
    startCenter: THREE.Vector3;
    startHalfSize: THREE.Vector3;
  } | null>(null);

  // Initialize box when enabled
  useEffect(() => {
    if (!enabled) {
      setInitialized(false);
      pointClouds.forEach((pc) => {
        pc.material.clipMode = ClipMode.DISABLED;
        pc.material.useClipBox = false;
        pc.material.setClipBoxes([]);
        pc.material.updateShaderSource();
      });
      return;
    }

    if (initialized) return;

    rotationRef.current.identity();
    baseRotationRef.current.identity();

    let center: THREE.Vector3;
    if (controls && 'getTarget' in controls) {
      center = (controls as any).getTarget(new THREE.Vector3());
    } else {
      const dir = new THREE.Vector3();
      camera.getWorldDirection(dir);
      center = camera.position.clone().add(dir.multiplyScalar(20));
    }

    const distToCamera = camera.position.distanceTo(center);
    let hs = distToCamera * 0.15;
    hs = Math.max(hs, 1);

    setBoxCenter(center.clone());
    setBoxHalfSize(new THREE.Vector3(hs, hs, hs));
    setInitialized(true);
  }, [enabled, pointClouds, camera, initialized]);

  // Apply clip boxes each frame
  useFrame(() => {
    if (!enabled || !initialized) return;

    const fullSize = boxHalfSize.clone().multiplyScalar(2);

    if (mode !== 'NONE') {
      const matrix = new THREE.Matrix4().compose(boxCenter, rotationRef.current, fullSize);
      const inverse = matrix.clone().invert();

      const clipBox: IClipBox = {
        box: new THREE.Box3(new THREE.Vector3(-0.5, -0.5, -0.5), new THREE.Vector3(0.5, 0.5, 0.5)),
        matrix,
        inverse,
        position: boxCenter.clone(),
      };

      pointClouds.forEach((pc) => {
        pc.material.clipMode = CLIP_MODE_MAP[mode];
        pc.material.useClipBox = true;
        pc.material.setClipBoxes([clipBox]);
      });
    } else {
      pointClouds.forEach((pc) => {
        pc.material.clipMode = ClipMode.DISABLED;
        pc.material.useClipBox = false;
        pc.material.setClipBoxes([]);
      });
    }

    // Update wireframe rotation
    if (wireframeRef.current) {
      wireframeRef.current.quaternion.copy(rotationRef.current);
    }

    // Update handle positions and scale them for visibility
    FACES.forEach((face, i) => {
      const mesh = handleRefs.current[i];
      if (!mesh) return;
      const pos = getFaceWorldPos(face, boxCenter, boxHalfSize, rotationRef.current);
      mesh.position.copy(pos);
      const dist = camera.position.distanceTo(pos);
      mesh.scale.setScalar(dist * 0.018);
    });

    const centerMesh = handleRefs.current[6];
    if (centerMesh) {
      centerMesh.position.copy(boxCenter);
      const dist = camera.position.distanceTo(boxCenter);
      centerMesh.scale.setScalar(dist * 0.018);
    }
  });

  const disableControls = () => {
    if (controls && 'enabled' in controls) controls.enabled = false;
  };

  const enableControls = () => {
    if (controls && 'enabled' in controls) controls.enabled = true;
  };

  // PivotControls onDrag: extract rotation from the local matrix.
  // autoTransform is true so PivotControls keeps its own gizmo rotated.
  // The wireframe/handles are siblings, not children, so they're unaffected.
  const onPivotDrag = useCallback((localMatrix: THREE.Matrix4) => {
    const q = new THREE.Quaternion();
    localMatrix.decompose(new THREE.Vector3(), q, new THREE.Vector3());
    // PivotControls starts from identity after remount, so compose with base
    rotationRef.current.copy(q).premultiply(baseRotationRef.current);
  }, []);

  // Face handle drag — resize along one local axis
  const startFaceDrag = (e: ThreeEvent<PointerEvent>, face: FaceDef) => {
    e.stopPropagation();
    disableControls();

    const handlePos = getFaceWorldPos(face, boxCenter, boxHalfSize, rotationRef.current);

    // The drag direction is the local axis rotated into world space
    const dragDir = new THREE.Vector3();
    dragDir[face.axis] = 1;
    dragDir.applyQuaternion(rotationRef.current);

    const camDir = camera.getWorldDirection(new THREE.Vector3());

    // Gram-Schmidt: plane normal perpendicular to drag axis, facing camera
    const planeNormal = camDir.clone().addScaledVector(dragDir, -camDir.dot(dragDir));
    if (planeNormal.lengthSq() < 0.001) {
      planeNormal.copy(camera.up.clone().addScaledVector(dragDir, -camera.up.dot(dragDir)));
    }
    planeNormal.normalize();

    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, handlePos);

    const startIntersection = new THREE.Vector3();
    raycaster.setFromCamera(getNDC(e.nativeEvent, renderer.domElement), camera);
    raycaster.ray.intersectPlane(plane, startIntersection);

    dragRef.current = {
      type: 'face',
      face,
      plane,
      startIntersection: startIntersection.clone(),
      startCenter: boxCenter.clone(),
      startHalfSize: boxHalfSize.clone(),
    };

    renderer.domElement.style.cursor = 'grabbing';

    const onPointerMove = (ev: PointerEvent) => {
      if (!dragRef.current || dragRef.current.type !== 'face') return;

      raycaster.setFromCamera(getNDC(ev, renderer.domElement), camera);
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(dragRef.current.plane, hit)) return;

      // Project world-space drag delta onto the rotated axis
      const worldDelta = hit.clone().sub(dragRef.current.startIntersection);
      const delta = worldDelta.dot(dragDir);

      // signedDelta: positive means the face moves outward (box gets bigger on this side)
      const signedDelta = face.sign * delta;
      const startAxisHalf = dragRef.current.startHalfSize[face.axis];
      const newAxisHalf = Math.max(startAxisHalf + signedDelta, MIN_BOX_SIZE / 2);
      const actualGrowth = newAxisHalf - startAxisHalf;

      // Update halfSize along this axis
      const newHalfSize = dragRef.current.startHalfSize.clone();
      newHalfSize[face.axis] = newAxisHalf;

      // Shift center along the ROTATED axis by half the growth
      // (only the dragged face moves; the opposite face stays put)
      const centerShift = dragDir.clone().multiplyScalar(face.sign * actualGrowth);
      const newCenter = dragRef.current.startCenter.clone().add(centerShift);

      setBoxCenter(newCenter);
      setBoxHalfSize(newHalfSize);
    };

    const onPointerUp = () => {
      dragRef.current = null;
      renderer.domElement.style.cursor = '';
      enableControls();
      setPivotKey((k) => k + 1);
      baseRotationRef.current.copy(rotationRef.current);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  // Center handle drag (translation)
  const startTranslateDrag = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    disableControls();

    const camDir = camera.getWorldDirection(new THREE.Vector3());
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, boxCenter);

    const startIntersection = new THREE.Vector3();
    raycaster.setFromCamera(getNDC(e.nativeEvent, renderer.domElement), camera);
    raycaster.ray.intersectPlane(plane, startIntersection);

    dragRef.current = {
      type: 'translate',
      plane,
      startIntersection: startIntersection.clone(),
      startCenter: boxCenter.clone(),
      startHalfSize: boxHalfSize.clone(),
    };

    renderer.domElement.style.cursor = 'move';

    const onPointerMove = (ev: PointerEvent) => {
      if (!dragRef.current || dragRef.current.type !== 'translate') return;

      raycaster.setFromCamera(getNDC(ev, renderer.domElement), camera);
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(dragRef.current.plane, hit)) return;

      const delta = hit.clone().sub(dragRef.current.startIntersection);
      setBoxCenter(dragRef.current.startCenter.clone().add(delta));
    };

    const onPointerUp = () => {
      dragRef.current = null;
      renderer.domElement.style.cursor = '';
      enableControls();
      setPivotKey((k) => k + 1);
      baseRotationRef.current.copy(rotationRef.current);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  if (!enabled || !initialized) return null;

  const center: [number, number, number] = [boxCenter.x, boxCenter.y, boxCenter.z];
  const fullSize: [number, number, number] = [boxHalfSize.x * 2, boxHalfSize.y * 2, boxHalfSize.z * 2];

  return (
    <>
      {/* PivotControls: rotation gizmo only. Remounts via key when center changes. */}
      <PivotControls
        key={pivotKey}
        offset={center}
        rotation={
          new THREE.Euler().setFromQuaternion(rotationRef.current).toArray().slice(0, 3) as [number, number, number]
        }
        disableAxes
        disableSliders
        disableScaling
        depthTest={false}
        onDrag={onPivotDrag}
        onDragStart={disableControls}
        onDragEnd={enableControls}
        axisColors={['#ff6666', '#66ff66', '#6688ff']}
        scale={fullSize[0] * 0.75}
      />

      {/* Wireframe box — we apply rotation ourselves since autoTransform is off */}
      <lineSegments
        ref={wireframeRef}
        position={center}
        scale={fullSize}
        quaternion={rotationRef.current}
        renderOrder={998}>
        <edgesGeometry args={[new THREE.BoxGeometry(1, 1, 1)]} />
        <lineBasicMaterial color='#ffcc00' transparent opacity={0.8} depthTest={false} />
      </lineSegments>

      {/* Center handle for translation */}
      <mesh
        ref={(el) => {
          handleRefs.current[6] = el;
        }}
        position={center}
        onPointerDown={startTranslateDrag}
        onPointerOver={() => {
          if (!dragRef.current) renderer.domElement.style.cursor = 'move';
        }}
        onPointerOut={() => {
          if (!dragRef.current) renderer.domElement.style.cursor = '';
        }}
        renderOrder={1000}>
        <sphereGeometry args={[1, 16, 16]} />
        <meshBasicMaterial color='#ffffff' transparent opacity={0.9} depthTest={false} />
      </mesh>

      {/* Face handles — positions updated in useFrame */}
      {FACES.map((face, i) => (
        <mesh
          key={`${face.axis}-${face.sign}`}
          ref={(el) => {
            handleRefs.current[i] = el;
          }}
          onPointerDown={(e) => startFaceDrag(e, face)}
          onPointerOver={() => {
            if (!dragRef.current) renderer.domElement.style.cursor = 'grab';
          }}
          onPointerOut={() => {
            if (!dragRef.current) renderer.domElement.style.cursor = '';
          }}
          renderOrder={999}>
          <sphereGeometry args={[1, 12, 12]} />
          <meshBasicMaterial color={AXIS_COLORS[face.axis]} transparent opacity={0.85} depthTest={false} />
        </mesh>
      ))}
    </>
  );
};
